import { BaseService } from "../../core/baseService";
import { ApiResult } from "../../core/types";
import {
  HotelPaymentSettingsDto,
  UpdatePaymentSettingsDto,
  ValidateCredentialsDto,
  TestConnectionResult,
  PaymentStatusResponse,
  ValidateCredentialsResponse,
  StripeConnectUrlResponse,
  StripeAccountStatusResponse,
  DisconnectStripeResponse,
  ProviderSwitchEligibility,
  CancelPendingOrdersResponse,
} from "../types/paymentSettings.types";

export interface IPaymentSettingsService {
  getSettings(): Promise<ApiResult<HotelPaymentSettingsDto>>;
  updateSettings(
    dto: UpdatePaymentSettingsDto,
  ): Promise<ApiResult<HotelPaymentSettingsDto>>;
  validateCredentials(
    dto: ValidateCredentialsDto,
  ): Promise<ApiResult<ValidateCredentialsResponse>>;
  testConnection(): Promise<ApiResult<TestConnectionResult>>;
  getPaymentStatus(): Promise<ApiResult<PaymentStatusResponse>>;
  checkProviderSwitchEligibility(): Promise<ApiResult<ProviderSwitchEligibility>>;
  cancelPendingPaymentOrders(): Promise<ApiResult<CancelPendingOrdersResponse>>;

  // Stripe Connect methods
  getStripeConnectUrl(
    returnUrl: string,
    refreshUrl: string,
  ): Promise<ApiResult<StripeConnectUrlResponse>>;
  getStripeAccountStatus(): Promise<ApiResult<StripeAccountStatusResponse>>;
  disconnectStripe(): Promise<ApiResult<DisconnectStripeResponse>>;
}

export class PaymentSettingsService
  extends BaseService
  implements IPaymentSettingsService
{
  async getSettings(): Promise<ApiResult<HotelPaymentSettingsDto>> {
    return this.handleRequest<HotelPaymentSettingsDto>(
      this.client.get("/api/admin/payment-settings"),
    );
  }

  async updateSettings(
    dto: UpdatePaymentSettingsDto,
  ): Promise<ApiResult<HotelPaymentSettingsDto>> {
    return this.handleRequest<HotelPaymentSettingsDto>(
      this.client.put("/api/admin/payment-settings", dto),
    );
  }

  async validateCredentials(
    dto: ValidateCredentialsDto,
  ): Promise<ApiResult<ValidateCredentialsResponse>> {
    return this.handleRequest<ValidateCredentialsResponse>(
      this.client.post("/api/admin/payment-settings/validate", dto),
    );
  }

  async testConnection(): Promise<ApiResult<TestConnectionResult>> {
    return this.handleRequest<TestConnectionResult>(
      this.client.post("/api/admin/payment-settings/test"),
    );
  }

  async getPaymentStatus(): Promise<ApiResult<PaymentStatusResponse>> {
    return this.handleRequest<PaymentStatusResponse>(
      this.client.get("/api/admin/payment-settings/status"),
    );
  }

  async checkProviderSwitchEligibility(): Promise<
    ApiResult<ProviderSwitchEligibility>
  > {
    return this.handleRequest<ProviderSwitchEligibility>(
      this.client.get("/api/admin/payment-settings/switch-eligibility"),
    );
  }

  async cancelPendingPaymentOrders(): Promise<
    ApiResult<CancelPendingOrdersResponse>
  > {
    return this.handleRequest<CancelPendingOrdersResponse>(
      this.client.post("/api/admin/payment-settings/cancel-pending-orders"),
    );
  }

  // Stripe Connect methods
  async getStripeConnectUrl(
    returnUrl: string,
    refreshUrl: string,
  ): Promise<ApiResult<StripeConnectUrlResponse>> {
    return this.handleRequest<StripeConnectUrlResponse>(
      this.client.post("/api/admin/payment-settings/stripe/connect", {
        returnUrl,
        refreshUrl,
      }),
    );
  }

  async getStripeAccountStatus(): Promise<
    ApiResult<StripeAccountStatusResponse>
  > {
    return this.handleRequest<StripeAccountStatusResponse>(
      this.client.get("/api/admin/payment-settings/stripe/status"),
    );
  }

  async disconnectStripe(): Promise<ApiResult<DisconnectStripeResponse>> {
    return this.handleRequest<DisconnectStripeResponse>(
      this.client.post("/api/admin/payment-settings/stripe/disconnect"),
    );
  }
}
